/**
 * Competitions list and selected competition workspace.
 *
 * The overview stays read-only and scannable. Clicking a competition replaces
 * the list with a full-page operational view instead of a drawer.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import {
  useAdminCompetitions,
  type AdminCompetitionRow,
  type AdminGameKey,
  type CompetitionsFilters,
  type CompetitionsSortKey,
  type CompetitionProductType,
  type CompetitionType,
  type CompetitionStatus,
  type CompetitionFormat,
} from './api';
import {
  ChipGroup,
  DASH,
  DataTable,
  DataTableShell,
  DateRange,
  Pagination,
  formatCompetitionFormat,
  formatTimestamp,
  toIsoEnd,
  toIsoStart,
} from './shared';
import { CompetitionDetailPage } from './CompetitionDetailPage';
import { CreateCompetitionDialog } from './CreateCompetitionDialog';

const PAGE_SIZE = 50;

const TYPE_OPTIONS: Array<{ value: CompetitionType | 'any'; label: string }> = [
  { value: 'any', label: 'All' },
  { value: 'official', label: 'Official' },
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
];

const STATUS_OPTIONS: Array<{ value: CompetitionStatus | 'any'; label: string }> = [
  { value: 'any', label: 'All' },
  { value: 'OPEN', label: 'OPEN' },
  { value: 'LOCKED', label: 'LOCKED' },
  { value: 'SCORED', label: 'SCORED' },
  { value: 'COMPLETED', label: 'COMPLETED' },
  { value: 'CANCELLED', label: 'CANCELLED' },
];

const FORMAT_OPTIONS: Array<{ value: CompetitionFormat | 'any'; label: string }> = [
  { value: 'any', label: 'All' },
  { value: 'ONE_OFF', label: 'ONE_OFF' },
  { value: 'SERIES', label: 'SERIES' },
];

const GAME_OPTIONS: Array<{ value: AdminGameKey | 'any'; label: string }> = [
  { value: 'any', label: 'All' },
  { value: 'football_epl2627', label: 'EPL 26/27' },
  { value: 'football_wc26', label: 'Cup 2026' },
];

const PRODUCT_OPTIONS: Array<{ value: CompetitionProductType | 'any'; label: string }> = [
  { value: 'any', label: 'All' },
  { value: 'fantasy', label: 'Fantasy' },
];

/**
 * Map a `game_key` to the short league badge shown on each row. Falls back
 * to the raw key for forward-compat when the backend ships a new mode the
 * UI hasn't been taught about yet.
 */
function leagueBadgeLabel(gameKey: string): string {
  if (gameKey === 'football_epl2627') return 'EPL';
  if (gameKey === 'football_wc26') return 'WC';
  return gameKey;
}

function isAdminGameKey(value: string | null): value is AdminGameKey {
  return value === 'football_epl2627' || value === 'football_wc26';
}

function typeBadgeVariant(t: CompetitionType): 'default' | 'secondary' | 'outline' {
  if (t === 'official') return 'default';
  if (t === 'private') return 'secondary';
  return 'outline';
}

function statusBadgeVariant(s: string): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (s === 'OPEN') return 'default';
  if (s === 'LOCKED') return 'secondary';
  if (s === 'COMPLETED') return 'outline';
  return 'outline';
}

export function AllCompetitionsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [type, setType] = useState<CompetitionsFilters['type']>('any');
  const [productType, setProductType] = useState<CompetitionsFilters['product_type']>('any');
  const [status, setStatus] = useState<CompetitionsFilters['status']>('any');
  const [format, setFormat] = useState<CompetitionsFilters['format']>('any');
  // Initialize the league filter from the URL so a refresh / shared link
  // preserves the operator's selection. Only `game_key` round-trips today;
  // other filters keep their previous local-state-only behavior (#357 scope).
  const initialGameKey = searchParams.get('game_key');
  const [gameKey, setGameKey] = useState<CompetitionsFilters['game_key']>(
    isAdminGameKey(initialGameKey) ? initialGameKey : 'any',
  );
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [sort, setSort] = useState<{ key: CompetitionsSortKey; dir: 'asc' | 'desc' }>({
    key: 'created_at',
    dir: 'desc',
  });

  const toggleSort = (key: string) => {
    const sortKey = key as CompetitionsSortKey;
    setSort((prev) =>
      prev.key === sortKey
        ? { key: sortKey, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key: sortKey, dir: 'desc' },
    );
    setOffset(0);
  };

  const filters: CompetitionsFilters = {
    q: q.trim() || undefined,
    type,
    product_type: productType,
    status,
    format,
    game_key: gameKey,
    from: toIsoStart(from),
    to: toIsoEnd(to),
    limit: PAGE_SIZE,
    offset,
    sort: sort.key,
    dir: sort.dir,
  };

  const updateGameKey = (next: CompetitionsFilters['game_key']) => {
    setGameKey(next);
    setOffset(0);
    const params = new URLSearchParams(searchParams);
    if (!next || next === 'any') {
      params.delete('game_key');
    } else {
      params.set('game_key', next);
    }
    setSearchParams(params, { replace: true });
  };

  const { data, isLoading, error } = useAdminCompetitions(filters);
  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  const selectedCompetitionId = searchParams.get('id') ?? searchParams.get('competition_id');

  const setSelectedCompetition = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'all');
    if (id) {
      next.set('id', id);
      next.delete('competition_id');
      next.delete('entry_id');
      next.delete('round');
      next.delete('view');
    } else {
      next.delete('id');
      next.delete('competition_id');
      next.delete('entry_id');
      next.delete('round');
      next.delete('view');
    }
    setSearchParams(next, { replace: true });
  };

  if (selectedCompetitionId) {
    return (
      <CompetitionDetailPage
        competitionId={selectedCompetitionId}
        onBack={() => setSelectedCompetition(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-[280px] flex-1">
          <Input
            placeholder="Search title, ID, slug, invite token, creator email…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
          />
        </div>
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {isLoading && !data ? '…' : `${total.toLocaleString()} competition${total === 1 ? '' : 's'}`}
        </span>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          Create competition
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-md border bg-card px-4 py-3">
        <ChipGroup
          label="Game"
          value={productType ?? 'any'}
          options={PRODUCT_OPTIONS}
          onChange={(v) => {
            setProductType(v as CompetitionsFilters['product_type']);
            setOffset(0);
          }}
        />
        <ChipGroup
          label="League"
          value={gameKey ?? 'any'}
          options={GAME_OPTIONS}
          onChange={(v) => updateGameKey(v as CompetitionsFilters['game_key'])}
        />
        <ChipGroup
          label="Type"
          value={type ?? 'any'}
          options={TYPE_OPTIONS}
          onChange={(v) => {
            setType(v as CompetitionsFilters['type']);
            setOffset(0);
          }}
        />
        <ChipGroup
          label="Status"
          value={status ?? 'any'}
          options={STATUS_OPTIONS}
          onChange={(v) => {
            setStatus(v as CompetitionsFilters['status']);
            setOffset(0);
          }}
        />
        <ChipGroup
          label="Format"
          value={format ?? 'any'}
          options={FORMAT_OPTIONS}
          onChange={(v) => {
            setFormat(v as CompetitionsFilters['format']);
            setOffset(0);
          }}
        />
        <DateRange
          from={from}
          to={to}
          onChange={({ from, to }) => {
            setFrom(from);
            setTo(to);
            setOffset(0);
          }}
        />
      </div>

      <DataTableShell loading={isLoading} error={error} empty={rows.length === 0}>
        <DataTable<AdminCompetitionRow>
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => setSelectedCompetition(r.id)}
          sort={sort}
          onSort={toggleSort}
          columns={[
            {
              key: 'title',
              label: 'Title',
              render: (r) => (
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="shrink-0">
                      {leagueBadgeLabel(r.game_key)}
                    </Badge>
                    <Badge
                      variant={r.product_type === 'predictions' ? 'default' : 'secondary'}
                      className="shrink-0 capitalize"
                    >
                      {r.product_type}
                    </Badge>
                    <span className="font-medium text-foreground">{r.title}</span>
                  </div>
                  {r.slug ? (
                    <span className="font-mono text-xs text-muted-foreground">{r.slug}</span>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'entrants',
              label: '# Entries',
              align: 'right',
              sortKey: 'entrants',
              render: (r) => r.entrant_count.toLocaleString(),
            },
            {
              key: 'results',
              label: '# Results',
              align: 'right',
              render: (r) => r.result_count.toLocaleString(),
            },
            {
              key: 'type',
              label: 'Type',
              render: (r) => (
                <Badge variant={typeBadgeVariant(r.type)} className="capitalize">
                  {r.type}
                </Badge>
              ),
            },
            {
              key: 'status',
              label: 'Status',
              render: (r) => <Badge variant={statusBadgeVariant(r.status)}>{r.status}</Badge>,
            },
            {
              key: 'format',
              label: 'Format',
              render: (r) => <span>{formatCompetitionFormat(r.format)}</span>,
            },
            {
              key: 'created_at',
              label: 'Created at',
              sortKey: 'created_at',
              render: (r) => formatTimestamp(r.created_at),
            },
            {
              key: 'created_by',
              label: 'Created by',
              render: (r) => {
                if (!r.created_by) return DASH;
                return (
                  <div className="flex flex-col">
                    <span>{r.created_by}</span>
                    {r.created_by_kind ? (
                      <span className="text-xs text-muted-foreground">{r.created_by_kind}</span>
                    ) : null}
                  </div>
                );
              },
            },
            {
              key: 'round_window',
              label: 'Round window',
              render: (r) => r.round_window,
            },
          ]}
        />
        <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
      </DataTableShell>

      <CreateCompetitionDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
